/**
 * One order, as the kitchen sees it.
 *
 * Read from across a room, so the two things that matter are large: how long
 * it has been waiting, and what has to be made. Everything else — the order
 * number, the table — is there to be found when someone goes looking, not to
 * compete for attention.
 *
 * Dishes are ticked off individually because they finish at different times
 * and at different stations. The ticket is only ready when all of them are.
 *
 * READ-ONLY FOR ANYONE WHO CANNOT COOK
 * ------------------------------------
 * A cashier can see this screen — useful for answering "how long?" — but
 * cannot advance a ticket, and the backend refuses if they try. Showing them
 * the buttons anyway would mean every tap fails, which is how people learn
 * that half the interface does not work.
 */
import { urgency, waited, LATE, WARM } from './waiting.js';
import { Button } from '../../components/ui/Button.jsx';

const TONE = {
  late: 'border-bad bg-bad-bg',
  warm: 'border-warn bg-warn-bg',
  calm: 'border-rule bg-surface',
};

const CLOCK_TONE = {
  late: 'text-bad',
  warm: 'text-warn',
  calm: 'text-ink-2',
};

export function TicketCard({ ticket, onAdvance, busy, readOnly = false }) {
  const state = urgency(ticket.elapsed_minutes);
  const items = ticket.items ?? [];

  return (
    <article className={`flex flex-col rounded-xl border-2 p-3 ${TONE[state]}`}>
      <header className="flex items-baseline justify-between gap-2">
        <div className="min-w-0">
          <h3 className="truncate font-semibold text-ink">{ticket.order_number}</h3>
          {/* Bold and a size up: a cook reads "Takeaway" or "Table 6" from
              across the pass, so it earns more than the small grey line the
              order number's metadata used to get. */}
          <p className="text-sm font-bold text-ink">
            {/* On a Foodpanda ticket, the number the rider's app shows ("Foodpanda #32"),
                so the pass can hand over the right bag. */}
            {ticket.order_type === 'foodpanda' && ticket.external_order_id
              ? `${readableType(ticket.order_type)} #${ticket.external_order_id}`
              : readableType(ticket.order_type)}
            {ticket.table_number && ` · Table ${ticket.table_number}`}
          </p>
        </div>

        <span
          className={`shrink-0 text-2xl font-bold tabular-nums ${CLOCK_TONE[state]}`}
          // The number is the point, but it needs saying for anyone who cannot
          // see the colour it is in.
          aria-label={`Waiting ${waited(ticket.elapsed_minutes)}${
            state === LATE ? ', late' : state === WARM ? ', getting late' : ''
          }`}
        >
          {waited(ticket.elapsed_minutes)}
        </span>
      </header>

      {/*
        A list to read, not a list to tick.

        Every dish used to be a button, and finishing a ticket meant tapping
        each one and then the card. A cook has their hands full and touches the
        screen with a knuckle; the ticking was work the kitchen did for the
        software rather than the other way round — and a cook who is behind
        skips it, which leaves the board asserting something untrue.

        What the board is for is knowing what to cook and how long it has been
        waiting. Both of those are here to be read.
      */}
      {/*
        Items a deal brought in are gathered under the deal's name, so a cook
        reads "Family Deal" once and its parts beneath it — rather than loose
        dishes they cannot tie back to the one order. Anything not from a deal
        sits on its own, as before.
      */}
      <ul className="mt-3 flex-1 space-y-1.5">
        {groupByDeal(items).map((block, i) =>
          block.deal ? (
            <li
              key={`deal-${i}`}
              className="rounded-lg border border-brand-400/40 bg-brand-400/10 p-2"
            >
              <p className="mb-1.5 px-1 font-semibold leading-snug text-brand-300">{block.deal}</p>
              <ul className="space-y-1.5">
                {block.items.map((item) => (
                  <ItemRow key={item.id} item={item} />
                ))}
              </ul>
            </li>
          ) : (
            <ItemRow key={block.item.id} item={block.item} />
          )
        )}
      </ul>

      {ticket.special_instructions && (
        <p className="mt-2 rounded-lg bg-warn-bg px-2.5 py-2 text-sm font-medium text-warn">
          {ticket.special_instructions}
        </p>
      )}

      {!readOnly && (
        <div className="mt-3">
          <NextStep ticket={ticket} onAdvance={onAdvance} busy={busy} />
        </div>
      )}
    </article>
  );
}

/**
 * The one button that moves this ticket along.
 *
 * A kitchen screen with six buttons per ticket is a screen where the wrong one
 * gets hit. There is exactly one next step at any moment, and it is labelled
 * with what it does rather than with the status it sets.
 */
function NextStep({ ticket, onAdvance, busy }) {
  /**
   * Two presses for a ticket, and no more.
   *
   * WHAT THIS REPLACED
   * ------------------
   * Four rungs — start, ready, packed or ready-to-carry, handed over — and
   * before any of them, a tap on every dish on the ticket. A busy cook was
   * asked for six or seven presses per order, with their hands full, on a
   * screen they touch with a knuckle.
   *
   * That is work the kitchen did for the software. And it does not survive
   * service: the cook who is behind skips the ticking, which is exactly when
   * the board most needs to be true, so the board stopped being trusted and
   * then stopped being used.
   *
   * There are two things the rest of the shop needs to know, and this asks for
   * exactly those. That it has been started, so the floor can tell a customer
   * their food is being cooked rather than lost. And that it is done, which is
   * the moment a waiter is told to come and fetch it.
   *
   * `new` and `accepted` both start, and both say so: they were once two
   * actions sharing one label, so the first press appeared to do nothing and
   * got pressed again.
   *
   * `ready` and `packed` are here because tickets already sitting in those
   * states existed before this change, and a ticket with no button is a ticket
   * that never leaves the board.
   */
  const step = {
    new: { action: 'start', label: 'Start cooking' },
    accepted: { action: 'start', label: 'Start cooking' },
    preparing: { action: 'complete', label: 'Hand over' },
    ready: { action: 'complete', label: 'Hand over' },
    packed: { action: 'complete', label: 'Hand over' },
  }[ticket.status];

  if (!step) return null;

  return (
    <Button
      size="lg"
      variant="primary"
      className="w-full"
      busy={busy}
      onClick={() => onAdvance(ticket.id, step.action)}
    >
      {step.label}
    </Button>
  );
}

/** One dish on the ticket. */
function ItemRow({ item }) {
  // Removed at the till before it was made. Kept on the ticket, struck through,
  // so a cook who already read it sees it is off — not silently gone, which is
  // how the small pizza gets made anyway.
  const cancelled = item.status === 'cancelled';
  return (
    <li
      className={`flex items-start gap-2 rounded-lg border p-2.5 ${
        cancelled ? 'border-rule/40 bg-surface-3/30 opacity-60' : 'border-rule bg-surface'
      }`}
    >
      <span className={`shrink-0 text-lg font-bold tabular-nums ${cancelled ? 'text-ink-3' : ''}`}>
        {item.quantity}×
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block font-medium leading-snug ${cancelled ? 'text-ink-3 line-through' : ''}`}>
          {item.product_name}
        </span>
        {cancelled ? (
          <span className="block text-xs font-semibold uppercase tracking-wide text-bad">
            Removed
          </span>
        ) : (
          <>
            {item.modifiers && <span className="block text-sm text-ink-2">{item.modifiers}</span>}
            {item.special_instructions && (
              <span className="mt-0.5 block text-sm font-medium text-warn">
                {item.special_instructions}
              </span>
            )}
          </>
        )}
      </span>
    </li>
  );
}

/**
 * Group a ticket's items so a deal's parts sit together under its name.
 *
 * Walks the list in order — a deal's items are written to the ticket together,
 * so they arrive contiguous — and gathers each run that shares a deal name into
 * one block; everything else stays a standalone item. Returns a flat list of
 * blocks, either { deal, items } or { item }.
 */
function groupByDeal(items) {
  const blocks = [];
  let run = null;
  for (const item of items) {
    if (item.deal_name) {
      if (run && run.deal === item.deal_name) {
        run.items.push(item);
      } else {
        run = { deal: item.deal_name, items: [item] };
        blocks.push(run);
      }
    } else {
      run = null;
      blocks.push({ item });
    }
  }
  return blocks;
}

function readableType(type) {
  return (
    {
      dine_in: 'Dine in',
      takeaway: 'Takeaway',
      delivery: 'Delivery',
      foodpanda: 'Foodpanda',
      phone_order: 'Phone',
      online: 'Online',
    }[type] ?? type
  );
}
